import React, { useEffect, useState, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  UploadSimple,
  Receipt,
  FilePdf,
  Image as ImageIcon,
  Link as LinkIcon,
  Copy,
  Trash,
  Clock,
  ShieldCheck,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ALLOWED_EXT = ["jpg", "jpeg", "png", "webp", "pdf"];
const MAX_BYTES = 15 * 1024 * 1024;

function humanSize(bytes) {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const diff = (new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    if (diff < 0) return null;
    if (diff < 1) {
      const hours = Math.max(1, Math.round(diff * 24));
      return `${hours} hour${hours === 1 ? "" : "s"}`;
    }
    return `${Math.ceil(diff)} day${Math.ceil(diff) === 1 ? "" : "s"}`;
  } catch (e) {
    return null;
  }
}

export default function PackageReceipts({ packageId, user, onActivity }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(null); // receipt object
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const fileRef = useRef(null);

  const canWrite = user && ["super_admin", "company_admin", "dispatcher", "customer"].includes(user.role);

  const load = async () => {
    try {
      const { data } = await api.get(`/packages/${packageId}/receipts`);
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (packageId) load();
    return () => {
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    };
    // eslint-disable-next-line
  }, [packageId]);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = (f.name || "").split(".").pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error("Allowed file types: JPG, PNG, WEBP, PDF");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("File exceeds 15 MB limit");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/packages/${packageId}/receipts`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Receipt uploaded");
      e.target.value = "";
      await load();
      if (onActivity) onActivity();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete receipt "${r.file_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/packages/${packageId}/receipts/${r.id}`);
      toast.success("Receipt deleted");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const handlePreview = async (r) => {
    setPreviewing(r);
    try {
      const resp = await api.get(`/packages/${packageId}/receipts/${r.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(url);
    } catch (e) {
      setPreviewBlobUrl(null);
    }
  };

  const closePreview = () => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl(null);
    setPreviewing(null);
  };

  const handleShare = async (r) => {
    try {
      const { data } = await api.post(`/packages/${packageId}/receipts/${r.id}/share`);
      // Copy to clipboard right away
      try {
        await navigator.clipboard.writeText(data.share_url);
        toast.success(
          data.regenerated
            ? "New share link copied — expires in 7 days"
            : "Active share link copied"
        );
      } catch (_) {
        toast.success(
          data.regenerated
            ? "Share link generated — expires in 7 days"
            : "Active share link shown below"
        );
      }
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const handleCopyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch (_) {
      // Fallback
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success("Link copied");
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5 mt-4" data-testid="package-receipts-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500 flex items-center gap-1.5">
            <Receipt size={14} weight="bold" /> Proof of Purchase Receipts
          </div>
          <div className="text-[11px] text-zinc-500 mt-1 max-w-xl">
            Upload purchase receipts so Customs can verify item value. Generate a 7-day share link
            to send the receipt to a customs officer or anyone outside the platform.
          </div>
        </div>
        {canWrite && (
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf,.jpg,.jpeg,.png,.webp,.pdf"
              onChange={onPickFile}
              className="hidden"
              data-testid="receipt-file-input"
            />
            <Button
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="bg-[#002BF6] hover:bg-[#0021C7] text-white"
              data-testid="upload-receipt-btn"
            >
              <UploadSimple size={14} weight="bold" className="mr-1.5" />
              {uploading ? "Uploading…" : "Upload receipt"}
            </Button>
          </div>
        )}
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="text-sm text-zinc-500 py-4">Loading receipts…</div>
        ) : items.length === 0 ? (
          <div className="border-2 border-dashed border-zinc-200 rounded-md py-8 text-center" data-testid="receipts-empty">
            <Receipt size={28} className="text-zinc-300 mx-auto mb-2" />
            <div className="text-sm text-zinc-600 font-medium">No receipts uploaded yet</div>
            <div className="text-[11px] text-zinc-400 mt-1">JPG, PNG, WEBP or PDF · up to 15 MB each</div>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {items.map((r) => {
              const isPdf = r.kind === "pdf";
              const canDelete =
                canWrite && (user.role !== "customer" || r.uploaded_by_id === user.id);
              return (
                <li
                  key={r.id}
                  className="py-3 flex items-start gap-3"
                  data-testid={`receipt-row-${r.id}`}
                >
                  <div
                    className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${
                      isPdf ? "bg-red-50 text-red-600" : "bg-sky-50 text-sky-600"
                    }`}
                  >
                    {isPdf ? <FilePdf size={20} weight="bold" /> : <ImageIcon size={20} weight="bold" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => handlePreview(r)}
                        className="text-sm font-medium text-zinc-900 hover:text-[#002BF6] hover:underline text-left truncate max-w-full"
                        data-testid={`receipt-name-${r.id}`}
                        title={r.file_name}
                      >
                        {r.file_name}
                      </button>
                      <span className="text-[11px] text-zinc-400">{humanSize(r.size)}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
                      Uploaded {r.uploaded_at ? new Date(r.uploaded_at).toLocaleString() : "—"}
                      {r.uploaded_by_name ? ` · by ${r.uploaded_by_name}` : ""}
                      {r.uploaded_by_role ? ` (${r.uploaded_by_role.replace("_", " ")})` : ""}
                    </div>
                    {r.share_active && r.share_token && (
                      <div className="mt-2 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5 text-[11px] text-emerald-900 flex-wrap">
                        <ShieldCheck size={12} weight="fill" className="text-emerald-600 shrink-0" />
                        <span className="font-medium">Share link active</span>
                        <span className="text-emerald-700">
                          <Clock size={11} weight="bold" className="inline mr-0.5" />
                          expires in {daysUntil(r.share_expires_at) || "soon"}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleCopyLink(`${window.location.origin}/receipt/${r.share_token}`)}
                          className="ml-auto inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 underline"
                          data-testid={`copy-receipt-link-${r.id}`}
                        >
                          <Copy size={11} weight="bold" /> Copy link
                        </button>
                      </div>
                    )}
                  </div>
                  {canWrite && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleShare(r)}
                        data-testid={`share-receipt-${r.id}`}
                        title={r.share_active ? "Copy current share link" : "Generate 7-day share link"}
                      >
                        <LinkIcon size={13} weight="bold" className="mr-1" />
                        {r.share_active ? "Copy link" : "Share"}
                      </Button>
                      {canDelete && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(r)}
                          data-testid={`delete-receipt-${r.id}`}
                          title="Delete receipt"
                        >
                          <Trash size={13} weight="bold" className="text-red-600" />
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Preview dialog */}
      <Dialog open={!!previewing} onOpenChange={(v) => { if (!v) closePreview(); }}>
        <DialogContent
          className="w-[calc(100vw-2rem)] sm:max-w-3xl max-h-[calc(100dvh-2rem)] flex flex-col p-0 overflow-hidden"
          data-testid="receipt-preview-dialog"
        >
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-zinc-100 shrink-0">
            <DialogTitle className="truncate pr-8">{previewing?.file_name || "Receipt"}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-auto bg-zinc-100">
            {previewBlobUrl && previewing?.kind === "image" && (
              <img src={previewBlobUrl} alt={previewing?.file_name} className="w-full h-auto" />
            )}
            {previewBlobUrl && previewing?.kind === "pdf" && (
              <object data={previewBlobUrl} type="application/pdf" className="w-full h-[70vh]">
                <div className="p-6 text-sm text-zinc-600">
                  PDF preview not supported in this browser.{" "}
                  <a href={previewBlobUrl} target="_blank" rel="noreferrer" className="text-[#002BF6] underline">
                    Open in new tab
                  </a>
                </div>
              </object>
            )}
            {!previewBlobUrl && previewing && (
              <div className="p-10 text-center text-sm text-zinc-500">
                Preview unavailable.{" "}
                {previewing.share_active ? (
                  <a
                    href={`${window.location.origin}/receipt/${previewing.share_token}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#002BF6] underline"
                  >
                    Open via share link
                  </a>
                ) : (
                  <span>Generate a share link to view the file in a new tab.</span>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
